import * as React from 'react'

import { Temporal } from '@js-temporal/polyfill'
import { format } from 'date-fns'
import { z } from 'zod'

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  XIcon,
  CriticalIcon,
  DateTimePicker,
  Dialog,
  Flex,
  Form,
  IconButton,
  Image,
  ImagePlusIcon,
  Input,
  Notice,
  Paragraph,
  Select,
  SendHorizontalIcon,
  SimpleSpinner,
  Switch,
  Text,
  toast,
  TrashIcon,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import {
  FileUploadConfig,
  UploadSource,
  UploadType,
  useUploader,
} from '@buffer-mono/uploader'

import { FileInput } from '~publish/components/FileInput'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import { ChangelogEntryContent } from '../ChangelogEntryContent'
import { ChangelogNotificationCard } from '../ChangelogNotificationCard'
import type { ChangelogEntryAudience } from '../useChangelog'
import { ChangelogContentEditor } from './ChangelogContentEditor'
import type { ChangelogContent } from './ChangelogContentEditor'

function isChangelogEntryAudience(
  value: string,
): value is ChangelogEntryAudience {
  return value === 'general' || value === 'beta' || value === 'alpha'
}
import {
  changelogTemplateValue,
  serializeChangelogToHtml,
} from './createChangelogEditor'
import { slugify } from './slugify'

import styles from './ChangelogEntryEditor.module.css'

import type { JSX } from 'react'

const handleSubmit = (e: React.FormEvent): void => {
  e.preventDefault()
  // Form submit is handled by button clicks, not form submission
}

export interface ChangelogEntryData {
  title: string
  content: ChangelogContent
  coverImageUrl?: string
  audience: ChangelogEntryAudience
  /** Optional custom publish date. When undefined, the BE applies its
   * `Date.now` default on create and leaves the existing value unchanged on
   * update. */
  publishedAt?: Date
  /** URL-safe slug. When undefined on create, the BE derives one from the
   * title; the editor's auto-sync UX keeps a preview in sync with the title
   * until the user types into the slug field. */
  slug?: string
}

interface ChangelogEntryEditorProps {
  initialData?: Partial<ChangelogEntryData>
  onSave?: (data: ChangelogEntryData) => void
  onCancel?: () => void
  onChange?: (data: ChangelogEntryData) => void
  hideActions?: boolean
  children?: React.ReactNode
}

const coverImageRestrictions = {
  maxNumberOfFiles: 1,
  allowedFileTypes: ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
  uploadConfig: new Map([...FileUploadConfig.IMAGE, ...FileUploadConfig.GIF]),
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/
const changelogEntrySchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(200, 'Title must be under 200 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(80, 'Slug must be under 80 characters')
    .regex(
      SLUG_PATTERN,
      'Use lowercase letters, numbers, and single hyphens (no spaces or symbols)',
    ),
})

/**
 * Reusable form error component with critical icon
 */
function FormError({ message }: { message: string }): JSX.Element {
  return (
    <Paragraph
      size="sm"
      color="critical"
      className={styles.formError}
      icon={<CriticalIcon size="small" color="critical" />}
    >
      {message}
    </Paragraph>
  )
}

/**
 * Reusable form caption component for inline guidance text
 */
function FormCaption({ message }: { message: string }): JSX.Element {
  return (
    <Paragraph size="sm" color="subtle">
      {message}
    </Paragraph>
  )
}

type EditorStep = 'edit' | 'confirm'

interface FormErrors {
  title?: string
  slug?: string
  coverImage?: string
  publishedAt?: string
}

function dateToZonedDateTime(date: Date): Temporal.ZonedDateTime {
  return Temporal.Instant.fromEpochMilliseconds(
    date.getTime(),
  ).toZonedDateTimeISO(CURRENT_TIME_ZONE)
}

/**
 * The form portion of the editor (cover image, title, content, audience).
 */
function EditorForm({
  onCoverImageUpload,
  onCoverImageRemove,
  onTitleChange,
  onSlugChange,
  onContentChange,
  onAudienceChange,
  onCustomPublishedAtToggle,
  onPublishedAtChange,
  coverImageUrl,
  title,
  slug,
  content,
  audience,
  customPublishedAtEnabled,
  publishedAt,
  initialPublishedAt,
  isUploading,
  errors,
}: {
  onCoverImageUpload: (files: File[]) => void
  onCoverImageRemove: () => void
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onContentChange: (data: ChangelogContent) => void
  onAudienceChange: (value: ChangelogEntryAudience) => void
  onCustomPublishedAtToggle: (enabled: boolean) => void
  onPublishedAtChange: (value: Temporal.ZonedDateTime | undefined) => void
  coverImageUrl?: string
  title: string
  slug: string
  content: ChangelogContent
  audience: ChangelogEntryAudience
  customPublishedAtEnabled: boolean
  publishedAt: Temporal.ZonedDateTime | undefined
  initialPublishedAt: Date | undefined
  isUploading: boolean
  errors: FormErrors
}): JSX.Element {
  // The picker only shows for scheduled (future) or brand-new entries —
  // already-published ones disable the toggle — so the min is unconditionally
  // start-of-today (no backdating).
  const minPublishedAt =
    Temporal.Now.zonedDateTimeISO(CURRENT_TIME_ZONE).startOfDay()
  const isAlreadyPublished =
    initialPublishedAt !== undefined &&
    initialPublishedAt.getTime() <= Date.now()

  return (
    <div className={styles.formContainer}>
      {/* Cover Image */}
      <Form.Field name="coverImage">
        <Form.Label>Cover image</Form.Label>
        {coverImageUrl ? (
          <div className={styles.coverImagePreview}>
            <Image
              src={coverImageUrl}
              alt="Cover preview"
              className={styles.coverImage}
            />
            <IconButton
              type="button"
              label="Remove cover image"
              variant="critical"
              size="large"
              className={styles.removeCoverButton}
              onClick={onCoverImageRemove}
            >
              <TrashIcon />
            </IconButton>
          </div>
        ) : (
          <FileInput
            onChange={(e): void => {
              if (e.target.files) {
                onCoverImageUpload(Array.from(e.target.files))
              }
            }}
            disabled={isUploading}
          >
            <UnstyledButton
              type="button"
              className={styles.coverUploadButton}
              aria-busy={isUploading}
              disabled={isUploading}
            >
              <VisuallyHidden>
                {isUploading ? 'Uploading' : 'Select cover image'}
              </VisuallyHidden>
              {isUploading ? (
                <Flex direction="column" gap="xs" align="center">
                  <SimpleSpinner size="medium" />
                  <Text color="subtle">Uploading...</Text>
                </Flex>
              ) : (
                <Flex direction="column" gap="xs" align="center">
                  <ImagePlusIcon size="medium" color="subtle" />
                  <Text color="subtle">
                    Drag & drop or <Text color="brand">select a file</Text>.
                  </Text>
                </Flex>
              )}
            </UnstyledButton>
          </FileInput>
        )}
        {errors.coverImage ? (
          <FormError message={errors.coverImage} />
        ) : (
          <FormCaption message="Recommended aspect ratio is 16:9. Maximum file size is 10 MB." />
        )}
      </Form.Field>

      {/* Title Input */}
      <Form.Field name="title">
        <Form.Label>Title</Form.Label>
        <Form.Control>
          <Input
            value={title}
            onChange={onTitleChange}
            placeholder="What's new?"
            aria-invalid={!!errors.title}
            size="large"
          />
        </Form.Control>
        {errors.title && <FormError message={errors.title} />}
      </Form.Field>

      {/* Slug Input */}
      <Form.Field name="slug">
        <Form.Label>URL slug</Form.Label>
        <Form.Control>
          <Input
            value={slug}
            onChange={onSlugChange}
            placeholder="shareable-summary-cards"
            aria-invalid={!!errors.slug}
          />
        </Form.Control>
        {errors.slug ? (
          <FormError message={errors.slug} />
        ) : (
          <FormCaption message="Used in the public URL. Auto-derived from the title until you edit it." />
        )}
      </Form.Field>

      {/* Rich Text Editor */}
      <Form.Field name="content">
        <Form.Label>Content</Form.Label>
        <ChangelogContentEditor
          initialValue={content.slateValue}
          onChange={onContentChange}
          placeholder="Describe the changes..."
        />
      </Form.Field>

      {/* Audience Select */}
      <Form.Field name="audience">
        <Form.Label>Audience</Form.Label>
        <Select
          name="audience"
          value={audience}
          onValueChange={(value): void => {
            if (isChangelogEntryAudience(value)) {
              onAudienceChange(value)
            }
          }}
        >
          <Form.Control>
            <Select.Trigger />
          </Form.Control>
          <Select.Content>
            <Select.Item value="general">General (all users)</Select.Item>
            <Select.Item value="beta">Beta users only</Select.Item>
            <Select.Item value="alpha">Alpha users only</Select.Item>
          </Select.Content>
        </Select>
        <FormCaption message="This controls who will see the changelog entry." />
      </Form.Field>

      {/* Custom Publish Date */}
      <Form.Field name="publishedAt">
        <Flex align="center" gap="sm">
          <Switch
            id="changelog-custom-publish-date"
            checked={customPublishedAtEnabled}
            onCheckedChange={onCustomPublishedAtToggle}
            disabled={isAlreadyPublished}
          />
          <Form.Label htmlFor="changelog-custom-publish-date">
            Set a custom publish date
          </Form.Label>
        </Flex>
        {customPublishedAtEnabled ? (
          <DateTimePicker
            value={publishedAt}
            onValueChange={onPublishedAtChange}
            timezone={CURRENT_TIME_ZONE}
            min={minPublishedAt}
          >
            <DateTimePicker.Calendar />
            <DateTimePicker.Separator />
            <DateTimePicker.TimeInput id="changelog-publish-time" />
          </DateTimePicker>
        ) : initialPublishedAt ? (
          <FormCaption
            message={
              isAlreadyPublished
                ? `Published on ${format(
                    initialPublishedAt,
                    "MMM d, yyyy 'at' h:mm a",
                  )}. The publish date can't be changed after an entry goes live.`
                : `Scheduled for ${format(
                    initialPublishedAt,
                    "MMM d, yyyy 'at' h:mm a",
                  )}.`
            }
          />
        ) : (
          <FormCaption message="Defaults to the moment this entry is published. Pick a future date and time to schedule it." />
        )}
        {errors.publishedAt && <FormError message={errors.publishedAt} />}
      </Form.Field>
    </div>
  )
}

/**
 * Confirmation view showing both previews before publishing.
 */
function ConfirmationView({
  title,
  content,
  coverImageUrl,
  audience,
  publishedAt,
}: {
  title: string
  content: string
  coverImageUrl?: string
  audience: ChangelogEntryAudience
  publishedAt?: Date
}): JSX.Element {
  return (
    <div className={styles.confirmationContainer}>
      {/* Full entry preview */}
      <div className={styles.previewSection}>
        <Text size="sm" className={styles.previewLabel}>
          Entry preview
        </Text>
        <div className={styles.previewCard}>
          <ChangelogEntryContent
            title={title}
            content={content}
            coverImageUrl={coverImageUrl}
            audience={audience}
            publishedAt={publishedAt}
          />
        </div>
      </div>

      {/* Notification card preview */}
      <div className={styles.previewSection}>
        <Text size="sm" className={styles.previewLabel}>
          Notification preview
        </Text>
        <ChangelogNotificationCard
          title={title}
          coverImageUrl={coverImageUrl}
          audience={audience}
          onOpen={(): void => {}}
          onDismiss={(): void => {}}
        />
      </div>
    </div>
  )
}

/**
 * Changelog entry editor with confirmation step before publishing.
 *
 * Flow:
 * 1. Edit mode - user fills in the form
 * 2. Click "Publish" - validates and shows confirmation
 * 3. Confirmation mode - shows both previews
 * 4. Click "Confirm Publish" - actually publishes
 */
export function ChangelogEntryEditor({
  initialData,
  onSave,
  onCancel,
  onChange,
  hideActions = false,
  children,
}: ChangelogEntryEditorProps): JSX.Element {
  // Step state
  const [step, setStep] = React.useState<EditorStep>('edit')

  // Form state
  const [title, setTitle] = React.useState(initialData?.title ?? '')
  const [slug, setSlug] = React.useState(
    initialData?.slug ?? slugify(initialData?.title ?? ''),
  )
  // While `false`, the slug auto-syncs from the title; once the editor types
  // into the slug field (or an entry with an existing slug is being edited),
  // we stop auto-syncing.
  const slugTouchedRef = React.useRef(initialData?.slug !== undefined)
  const [content, setContent] = React.useState<ChangelogContent>(() => {
    // If we have initial content, use it; otherwise use the template with its HTML
    if (initialData?.content) {
      return initialData.content
    }
    return {
      html: serializeChangelogToHtml(changelogTemplateValue),
      slateValue: changelogTemplateValue,
    }
  })
  const [coverImageUrl, setCoverImageUrl] = React.useState<string | undefined>(
    initialData?.coverImageUrl,
  )
  const [audience, setAudience] = React.useState<ChangelogEntryAudience>(
    initialData?.audience ?? 'general',
  )

  // Custom publish date state. The toggle starts on only for *scheduled*
  // entries (publishedAt is in the future) so the admin can adjust the
  // schedule. Already-published entries can't change their publish date, and
  // brand-new entries default to off — when off, the mutation omits
  // `publishedAt` and the BE applies its default on create / leaves the value
  // unchanged on update.
  const initialIsScheduled =
    initialData?.publishedAt !== undefined &&
    initialData.publishedAt.getTime() > Date.now()
  const [customPublishedAtEnabled, setCustomPublishedAtEnabled] =
    React.useState(initialIsScheduled)
  const [publishedAt, setPublishedAt] = React.useState<
    Temporal.ZonedDateTime | undefined
  >(
    initialIsScheduled && initialData?.publishedAt
      ? dateToZonedDateTime(initialData.publishedAt)
      : undefined,
  )

  // Upload state
  const [isUploading, setIsUploading] = React.useState(false)

  // Validation state
  const [errors, setErrors] = React.useState<FormErrors>({})

  // Use ref to avoid re-renders when parent passes inline arrow function
  const onChangeRef = React.useRef(onChange)
  React.useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Notify parent of changes
  React.useEffect(() => {
    onChangeRef.current?.({ title, content, coverImageUrl, audience, slug })
  }, [title, content, coverImageUrl, audience, slug])

  // Uploader setup
  const accountId = useAccountId()
  const organizationId = useOrganizationId()

  const uploader = useUploader({
    id: UploadType.postAsset,
    userId: accountId ?? '',
    organizationId: organizationId ?? '',
    fileRestrictions: coverImageRestrictions,
  })

  const handleCoverImageUpload = React.useCallback(
    async (files: File[]) => {
      if (!files.length) return

      const file = files[0]

      // Clear previous error
      setErrors((prev) => ({ ...prev, coverImage: undefined }))

      if (file.size > MAX_FILE_SIZE) {
        setErrors((prev) => ({
          ...prev,
          coverImage: 'Cover image must be under 10 MB.',
        }))
        return
      }

      setIsUploading(true)

      try {
        const result = await uploader.upload([file], {
          source: UploadSource.filePicker(),
        })

        if (result.successful.length) {
          setCoverImageUrl(result.successful[0].uploadURL)
          toast.success('Cover image uploaded!')
        } else {
          setErrors((prev) => ({
            ...prev,
            coverImage: 'Upload failed. Please try again.',
          }))
        }
      } catch {
        setErrors((prev) => ({
          ...prev,
          coverImage: 'Upload failed. Please try again.',
        }))
      } finally {
        setIsUploading(false)
        uploader.uppyInstance.cancelAll()
      }
    },
    [uploader],
  )

  const handleRemoveCoverImage = React.useCallback(() => {
    setCoverImageUrl(undefined)
  }, [])

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const nextTitle = e.target.value
    setTitle(nextTitle)
    if (!slugTouchedRef.current) {
      setSlug(slugify(nextTitle))
      if (errors.slug) {
        setErrors((prev) => ({ ...prev, slug: undefined }))
      }
    }
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: undefined }))
    }
  }

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    slugTouchedRef.current = true
    setSlug(e.target.value)
    if (errors.slug) {
      setErrors((prev) => ({ ...prev, slug: undefined }))
    }
  }

  const handleContentChange = (data: ChangelogContent): void => {
    setContent(data)
  }

  const handleCustomPublishedAtToggle = (enabled: boolean): void => {
    setCustomPublishedAtEnabled(enabled)
    if (errors.publishedAt) {
      setErrors((prev) => ({ ...prev, publishedAt: undefined }))
    }
    if (enabled && !publishedAt) {
      // Pre-fill with the entry's current value if editing, otherwise "now",
      // so the picker has a sensible starting point.
      setPublishedAt(
        initialData?.publishedAt
          ? dateToZonedDateTime(initialData.publishedAt)
          : Temporal.Now.zonedDateTimeISO(CURRENT_TIME_ZONE),
      )
    }
  }

  const handlePublishedAtChange = (
    value: Temporal.ZonedDateTime | undefined,
  ): void => {
    setPublishedAt(value)
    if (errors.publishedAt) {
      setErrors((prev) => ({ ...prev, publishedAt: undefined }))
    }
  }

  const validateAndProceed = (): boolean => {
    const result = changelogEntrySchema.safeParse({ title, slug })
    const fieldErrors: FormErrors = {}

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0]
        if (field === 'title') {
          fieldErrors.title = issue.message
        } else if (field === 'slug') {
          fieldErrors.slug = issue.message
        }
      }
    }

    if (customPublishedAtEnabled && !publishedAt) {
      fieldErrors.publishedAt =
        'Pick a date and time, or turn off the custom publish date.'
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors)
      return false
    }

    setErrors({})
    return true
  }

  const handlePublishClick = (): void => {
    if (validateAndProceed()) {
      setStep('confirm')
    }
  }

  const handleBackToEdit = (): void => {
    setStep('edit')
  }

  const handleConfirmPublish = (): void => {
    onSave?.({
      title,
      content,
      coverImageUrl,
      audience,
      slug,
      publishedAt:
        customPublishedAtEnabled && publishedAt
          ? new Date(publishedAt.epochMilliseconds)
          : undefined,
    })
  }

  // The entry is "scheduled" when the picker is on and the chosen time hasn't
  // arrived yet — drives the confirm-step button label and notice copy.
  const isScheduledPublish =
    customPublishedAtEnabled &&
    publishedAt !== undefined &&
    publishedAt.epochMilliseconds > Date.now()

  return (
    <Form onSubmit={handleSubmit} className={styles.container}>
      {/* Render children (e.g., Dialog.Header) - only in edit step */}
      {step === 'edit' && children}

      {/* Confirmation header */}
      {step === 'confirm' && (
        <Dialog.Header>
          <Flex
            align="center"
            justify="between"
            className={styles.confirmHeader}
          >
            <IconButton
              variant="tertiary"
              size="medium"
              label="Back to edit"
              onClick={handleBackToEdit}
            >
              <ArrowLeftIcon />
            </IconButton>
            <Dialog.Title className={styles.centeredTitle}>
              Preview
            </Dialog.Title>
            <Dialog.Close>
              <IconButton variant="tertiary" size="medium" label="Close">
                <XIcon />
              </IconButton>
            </Dialog.Close>
          </Flex>
        </Dialog.Header>
      )}

      {/* Edit step content */}
      {step === 'edit' && (
        <div className={styles.editorContent}>
          <EditorForm
            onCoverImageUpload={handleCoverImageUpload}
            onCoverImageRemove={handleRemoveCoverImage}
            onTitleChange={handleTitleChange}
            onSlugChange={handleSlugChange}
            onContentChange={handleContentChange}
            onAudienceChange={setAudience}
            onCustomPublishedAtToggle={handleCustomPublishedAtToggle}
            onPublishedAtChange={handlePublishedAtChange}
            coverImageUrl={coverImageUrl}
            title={title}
            slug={slug}
            content={content}
            audience={audience}
            customPublishedAtEnabled={customPublishedAtEnabled}
            publishedAt={publishedAt}
            initialPublishedAt={initialData?.publishedAt}
            isUploading={isUploading}
            errors={errors}
          />
        </div>
      )}

      {/* Confirmation step content */}
      {step === 'confirm' && (
        <div className={styles.editorContent}>
          {/* Notice about visibility */}
          <div className={styles.editorNotice}>
            <Notice variant="info">
              This entry will be visible to{' '}
              {
                {
                  general: 'all Buffer users',
                  beta: 'Beta users only',
                  alpha: 'Alpha users only',
                }[audience]
              }{' '}
              {isScheduledPublish && publishedAt
                ? `on ${format(
                    new Date(publishedAt.epochMilliseconds),
                    "MMM d, yyyy 'at' h:mm a",
                  )}.`
                : 'once published.'}
            </Notice>
          </div>
          <ConfirmationView
            title={title}
            content={content.html}
            coverImageUrl={coverImageUrl}
            audience={audience}
            publishedAt={
              customPublishedAtEnabled && publishedAt
                ? new Date(publishedAt.epochMilliseconds)
                : initialData?.publishedAt
            }
          />
        </div>
      )}

      {/* Actions - Edit step */}
      {!hideActions && step === 'edit' && (
        <Dialog.Footer className={styles.footer}>
          {onCancel && (
            <Button
              type="button"
              variant="tertiary"
              size="large"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="large"
            disabled={isUploading}
            onClick={handlePublishClick}
          >
            Preview
            <ArrowRightIcon />
          </Button>
        </Dialog.Footer>
      )}

      {/* Actions - Confirmation step */}
      {!hideActions && step === 'confirm' && (
        <Dialog.Footer className={styles.footer}>
          <Button
            type="button"
            variant="tertiary"
            size="large"
            onClick={handleBackToEdit}
          >
            Back
          </Button>
          <Button
            type="button"
            variant="primary"
            size="large"
            onClick={handleConfirmPublish}
          >
            <SendHorizontalIcon />
            {isScheduledPublish ? 'Schedule' : 'Publish'}
          </Button>
        </Dialog.Footer>
      )}
    </Form>
  )
}
